import { Component, inject, signal, computed, ElementRef, HostListener } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  LocaleService,
  SupportedLocale,
  LOCALE_OPTIONS,
  LocaleOption,
} from '../../core/locale.service';

/**
 * Language switcher dropdown component.
 *
 * Displays the currently active locale using its native language name
 * and provides a dropdown to switch between supported locales.
 * Fully accessible with keyboard navigation and ARIA attributes.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.7, 8.2, 8.3
 */
@Component({
    selector: 'app-language-switcher',
    imports: [TranslateModule],
    templateUrl: './language-switcher.component.html',
    styleUrls: ['./language-switcher.component.scss']
})
export class LanguageSwitcherComponent {
  private readonly localeService = inject(LocaleService);
  private readonly elementRef = inject(ElementRef);

  /** Whether the dropdown is open */
  readonly isOpen = signal(false);

  /** Currently active locale */
  readonly activeLocale = signal<SupportedLocale>(this.localeService.currentLocale);

  /** Index of the currently focused option in the dropdown */
  readonly focusedIndex = signal(-1);

  /** Error message to display when locale switch fails */
  readonly errorMessage = signal<string | null>(null);

  /** Available locale options */
  readonly localeOptions: LocaleOption[] = LOCALE_OPTIONS;

  /** The label of the currently active locale */
  readonly activeLabel = computed(() => {
    const locale = this.activeLocale();
    const option = this.localeOptions.find(o => o.code === locale);
    return option?.label ?? 'English';
  });

  private localeSubscription: Subscription | null = null;

  ngOnInit(): void {
    this.localeSubscription = this.localeService.locale$.subscribe(locale => {
      this.activeLocale.set(locale);
    });
  }

  ngOnDestroy(): void {
    this.localeSubscription?.unsubscribe();
  }

  /** Toggle the dropdown open/closed */
  toggleDropdown(): void {
    const opening = !this.isOpen();
    this.isOpen.set(opening);
    if (opening) {
      // Focus the currently active locale option
      const activeIndex = this.localeOptions.findIndex(o => o.code === this.activeLocale());
      this.focusedIndex.set(activeIndex >= 0 ? activeIndex : 0);
    } else {
      this.focusedIndex.set(-1);
    }
  }

  /** Close the dropdown */
  closeDropdown(): void {
    this.isOpen.set(false);
    this.focusedIndex.set(-1);
  }

  /** Select a locale option */
  selectLocale(locale: SupportedLocale): void {
    this.errorMessage.set(null);
    this.closeDropdown();

    if (locale === this.activeLocale()) {
      return;
    }

    this.localeService.switchLocale(locale).subscribe({
      next: () => {
        // Success — locale updated via subscription
      },
      error: (err: Error) => {
        this.errorMessage.set(err.message || 'Language switch failed. Please try again.');
      },
    });
  }

  /** Handle keyboard events on the trigger button */
  onTriggerKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggleDropdown();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen()) {
          this.toggleDropdown();
        } else {
          this.moveFocus(1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen()) {
          this.toggleDropdown();
        } else {
          this.moveFocus(-1);
        }
        break;
      case 'Escape':
        if (this.isOpen()) {
          event.preventDefault();
          this.closeDropdown();
        }
        break;
    }
  }

  /** Handle keyboard events on the listbox options */
  onListKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocus(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveFocus(-1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.focusedIndex() >= 0) {
          this.selectLocale(this.localeOptions[this.focusedIndex()].code);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
      case 'Tab':
        this.closeDropdown();
        break;
    }
  }

  /** Move focus up or down in the options list */
  private moveFocus(direction: number): void {
    const currentIndex = this.focusedIndex();
    const maxIndex = this.localeOptions.length - 1;
    let newIndex = currentIndex + direction;

    if (newIndex < 0) {
      newIndex = maxIndex;
    } else if (newIndex > maxIndex) {
      newIndex = 0;
    }

    this.focusedIndex.set(newIndex);
  }

  /** Close dropdown when clicking outside */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen() && !this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }
}
